import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Download, Loader2, Baby, CalendarCheck, CalendarOff,
  GraduationCap, Trophy, Clock, CheckCircle2, XCircle, TrendingUp, FileText,
  Share2, Link, MessageCircle, Check
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDaysInMonth } from "date-fns";
import { ar } from "date-fns/locale";
import PerformanceTrendChart from "./PerformanceTrendChart";

const months = [
  { value: "0", label: "يناير" }, { value: "1", label: "فبراير" }, { value: "2", label: "مارس" },
  { value: "3", label: "أبريل" }, { value: "4", label: "مايو" }, { value: "5", label: "يونيو" },
  { value: "6", label: "يوليو" }, { value: "7", label: "أغسطس" }, { value: "8", label: "سبتمبر" },
  { value: "9", label: "أكتوبر" }, { value: "10", label: "نوفمبر" }, { value: "11", label: "ديسمبر" },
];

interface Props {
  fixedChildId?: string;
  fixedChildName?: string;
}

export default function MonthlyChildReport({ fixedChildId, fixedChildName }: Props = {}) {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const now = new Date();
  const [selectedChildId, setSelectedChildId] = useState(fixedChildId || "");
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth()));
  const [selectedYear] = useState(String(now.getFullYear()));
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const monthDate = new Date(parseInt(selectedYear), parseInt(selectedMonth), 1);
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const monthLabel = format(monthDate, "MMMM yyyy", { locale: ar });

  // Fetch children (only when no fixedChildId)
  const { data: children } = useQuery({
    queryKey: ["report-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("children")
        .select("id, full_name, date_of_birth, classes(name)")
        .eq("nursery_id", nurseryId!)
        .order("full_name");
      return data || [];
    },
    enabled: !!nurseryId && !fixedChildId,
  });

  // Fetch attendance for selected child & month
  const { data: attendance, isLoading: attLoading } = useQuery({
    queryKey: ["report-attendance", selectedChildId, selectedMonth, selectedYear],
    queryFn: async () => {
      const { data } = await supabase
        .from("attendance")
        .select("*")
        .eq("attendee_id", selectedChildId)
        .eq("attendee_type", "child")
        .gte("date", format(monthStart, "yyyy-MM-dd"))
        .lte("date", format(monthEnd, "yyyy-MM-dd"));
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Fetch leave requests
  const { data: leaves } = useQuery({
    queryKey: ["report-leaves", selectedChildId, selectedMonth, selectedYear],
    queryFn: async () => {
      const { data } = await supabase
        .from("leave_requests")
        .select("*")
        .eq("child_id", selectedChildId)
        .eq("status", "approved")
        .lte("start_date", format(monthEnd, "yyyy-MM-dd"))
        .gte("end_date", format(monthStart, "yyyy-MM-dd"));
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Fetch exam submissions
  const { data: examSubmissions } = useQuery({
    queryKey: ["report-exams", selectedChildId, selectedMonth, selectedYear],
    queryFn: async () => {
      const { data } = await supabase
        .from("exam_submissions")
        .select("*, exams(title)")
        .eq("child_id", selectedChildId)
        .eq("status", "completed")
        .gte("submitted_at", monthStart.toISOString())
        .lte("submitted_at", monthEnd.toISOString())
        .order("submitted_at");
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Fetch game results
  const { data: gameResults } = useQuery({
    queryKey: ["report-games", selectedChildId, selectedMonth, selectedYear],
    queryFn: async () => {
      const { data } = await supabase
        .from("game_results")
        .select("*")
        .eq("child_id", selectedChildId)
        .gte("played_at", monthStart.toISOString())
        .lte("played_at", monthEnd.toISOString())
        .order("played_at");
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  const selectedChild = fixedChildId
    ? { id: fixedChildId, full_name: fixedChildName || "", classes: null }
    : children?.find((c: any) => c.id === selectedChildId);

  // Compute stats
  const totalDays = getDaysInMonth(monthDate);
  const presentDays = (attendance || []).filter((a: any) => a.status === "present").length;
  const lateDays = (attendance || []).filter((a: any) => a.status === "late").length;
  const absentDays = (attendance || []).filter((a: any) => a.status === "absent").length;
  const attendanceRate = totalDays > 0 ? Math.round(((presentDays + lateDays) / totalDays) * 100) : 0;

  const leaveCount = (leaves || []).length;
  const examCount = (examSubmissions || []).length;
  const examAvg = examCount > 0
    ? Math.round((examSubmissions || []).reduce((sum: number, e: any) => sum + (e.percentage || 0), 0) / examCount)
    : 0;

  const gameCount = (gameResults || []).length;
  const gameAvg = gameCount > 0
    ? Math.round((gameResults || []).reduce((sum: number, g: any) => sum + (g.percentage || 0), 0) / gameCount)
    : 0;

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current || (!selectedChild && !fixedChildId)) return;
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = 190;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = 277;

      let position = 10;
      let heightLeft = pdfHeight;

      pdf.addImage(imgData, "PNG", 10, position, pdfWidth, pdfHeight);
      heightLeft -= (pageHeight - position);

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, -(pdfHeight - heightLeft), pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`تقرير-${selectedChild?.full_name || fixedChildName || "طفل"}-${format(monthDate, "yyyy-MM")}.pdf`);
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      setExporting(false);
    }
  }, [selectedChild, monthDate]);

  const handleCopyLink = useCallback(() => {
    const url = new URL(window.location.href);
    if (fixedChildId) {
      url.searchParams.set("month", selectedMonth);
    } else {
      url.searchParams.set("child", selectedChildId);
      url.searchParams.set("month", selectedMonth);
    }
    navigator.clipboard.writeText(url.toString());
    toast.success("تم نسخ الرابط بنجاح");
  }, [selectedChildId, selectedMonth, fixedChildId]);

  const handleShareWhatsApp = useCallback(() => {
    const childName = selectedChild?.full_name || fixedChildName || "الطفل";
    const url = new URL(window.location.href);
    if (!fixedChildId) url.searchParams.set("child", selectedChildId);
    url.searchParams.set("month", selectedMonth);
    const text = `📋 التقرير الشهري - ${childName}\n📅 ${monthLabel}\n\n📊 نسبة الحضور: ${attendanceRate}%\n📝 معدل الامتحانات: ${examCount > 0 ? examAvg + "%" : "لا يوجد"}\n🎮 معدل الألعاب: ${gameCount > 0 ? gameAvg + "%" : "لا يوجد"}\n\n🔗 عرض التقرير:\n${url.toString()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  }, [selectedChild, selectedChildId, selectedMonth, monthLabel, attendanceRate, examAvg, examCount, gameAvg, gameCount, fixedChildId, fixedChildName]);

  const isLoading = attLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            التقرير الشهري الشامل
          </h2>
          <p className="text-muted-foreground text-xs mt-1">تقرير مفصل يشمل الحضور والإجازات والامتحانات</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedChildId && !isLoading && (
            <>
              <Button variant="outline" size="sm" onClick={handleCopyLink} className="gap-1.5">
                <Link className="w-4 h-4" />
                نسخ الرابط
              </Button>
              <Button variant="outline" size="sm" onClick={handleShareWhatsApp} className="gap-1.5 text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <MessageCircle className="w-4 h-4" />
                واتساب
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} className="gap-1.5">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                تحميل PDF
              </Button>
            </>
          )}
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!fixedChildId && (
            <Select value={selectedChildId} onValueChange={setSelectedChildId}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="اختر طفل..." /></SelectTrigger>
              <SelectContent>
                {(children || []).map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {!selectedChildId ? (
        <Card className="shadow-card">
          <CardContent className="py-16 text-center">
            <Baby className="w-14 h-14 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">اختر طفل لعرض تقريره الشهري</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div ref={reportRef} className="space-y-4 bg-background p-4 rounded-xl">
          {/* Report Header */}
          <div className="text-center border-b pb-4 space-y-1">
            <h3 className="text-xl font-bold text-foreground">التقرير الشهري</h3>
            <p className="text-primary font-semibold">{selectedChild?.full_name}</p>
            <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <span>{monthLabel}</span>
              {selectedChild?.classes && <Badge variant="outline" className="text-[10px]">{(selectedChild.classes as any)?.name}</Badge>}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "نسبة الحضور", value: `${attendanceRate}%`, icon: CalendarCheck, color: attendanceRate >= 80 ? "text-primary" : "text-destructive" },
              { label: "أيام الإجازة", value: leaveCount, icon: CalendarOff, color: "text-amber-500" },
              { label: "معدل الامتحانات", value: examCount > 0 ? `${examAvg}%` : "-", icon: GraduationCap, color: examAvg >= 70 ? "text-primary" : "text-destructive" },
              { label: "معدل الألعاب", value: gameCount > 0 ? `${gameAvg}%` : "-", icon: Trophy, color: "text-amber-500" },
            ].map((s, i) => (
              <Card key={i} className="shadow-card">
                <CardContent className="p-3 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <s.icon className={`w-4 h-4 ${s.color}`} />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-foreground">{s.value}</div>
                    <div className="text-[10px] text-muted-foreground">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Attendance Section */}
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalendarCheck className="w-4 h-4 text-primary" />
                سجل الحضور - {monthLabel}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <span className="flex items-center gap-1 text-primary">
                  <CheckCircle2 className="w-3 h-3" /> حاضر: {presentDays}
                </span>
                <span className="flex items-center gap-1 text-amber-500">
                  <Clock className="w-3 h-3" /> متأخر: {lateDays}
                </span>
                <span className="flex items-center gap-1 text-destructive">
                  <XCircle className="w-3 h-3" /> غائب: {absentDays}
                </span>
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>نسبة الحضور</span>
                  <span>{attendanceRate}%</span>
                </div>
                <Progress value={attendanceRate} className="h-2" />
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1 text-center text-[10px]">
                {["أحد", "اثن", "ثلا", "أرب", "خمي", "جمع", "سبت"].map((d) => (
                  <div key={d} className="font-medium text-muted-foreground py-1">{d}</div>
                ))}
                {(() => {
                  const firstDay = monthStart.getDay();
                  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                  const cells = [];
                  for (let i = 0; i < firstDay; i++) {
                    cells.push(<div key={`empty-${i}`} />);
                  }
                  days.forEach((day) => {
                    const dayStr = format(day, "yyyy-MM-dd");
                    const record = (attendance || []).find((a: any) => a.date === dayStr);
                    const bg = record
                      ? record.status === "present" ? "bg-primary/20 text-primary"
                        : record.status === "late" ? "bg-amber-100 text-amber-700"
                        : "bg-destructive/15 text-destructive"
                      : "text-muted-foreground/60";
                    cells.push(
                      <div key={dayStr} className={`rounded py-1 ${bg}`}>
                        {day.getDate()}
                      </div>
                    );
                  });
                  return cells;
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Leaves Section */}
          {leaveCount > 0 && (
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CalendarOff className="w-4 h-4 text-amber-500" />
                  الإجازات المعتمدة ({leaveCount})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {(leaves || []).map((leave: any) => (
                    <div key={leave.id} className="py-2 flex items-center justify-between text-sm">
                      <div>
                        <Badge variant="outline" className="text-[10px] ml-2">
                          {leave.leave_type === "sick" ? "مرضي" : leave.leave_type === "personal" ? "شخصي" : leave.leave_type === "family" ? "عائلي" : leave.leave_type === "travel" ? "سفر" : "أخرى"}
                        </Badge>
                        {leave.reason && <span className="text-xs text-muted-foreground">{leave.reason}</span>}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(leave.start_date), "d MMM", { locale: ar })} → {format(new Date(leave.end_date), "d MMM", { locale: ar })}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exams Section */}
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-primary" />
                نتائج الامتحانات ({examCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {examCount === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">لا توجد امتحانات هذا الشهر</p>
              ) : (
                <div className="divide-y">
                  {(examSubmissions || []).map((exam: any) => (
                    <div key={exam.id} className="py-2 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{exam.exams?.title || "امتحان"}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {exam.submitted_at && format(new Date(exam.submitted_at), "d MMM yyyy", { locale: ar })}
                        </p>
                      </div>
                      <div className="text-left shrink-0">
                        <span className={`text-lg font-bold ${(exam.percentage || 0) >= 70 ? "text-primary" : (exam.percentage || 0) >= 50 ? "text-amber-500" : "text-destructive"}`}>
                          {exam.percentage != null ? `${Math.round(exam.percentage)}%` : "-"}
                        </span>
                        <p className="text-[10px] text-muted-foreground">{exam.total_score ?? 0}/{exam.max_score ?? 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {examCount > 0 && (
                <div className="mt-3 pt-3 border-t flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">المعدل العام</span>
                  <div className="flex items-center gap-2">
                    <Progress value={examAvg} className="h-2 w-24" />
                    <span className={`text-sm font-bold ${examAvg >= 70 ? "text-primary" : "text-destructive"}`}>{examAvg}%</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Games Section */}
          {gameCount > 0 && (
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  الألعاب التعليمية ({gameCount})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {(gameResults || []).map((game: any) => (
                    <div key={game.id} className="py-2 flex items-center justify-between text-sm">
                      <div>
                        <p className="font-medium text-foreground">{game.game_type}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {format(new Date(game.played_at), "d MMM", { locale: ar })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{game.score}/{game.max_score}</Badge>
                        <span className="text-xs">{"⭐".repeat(game.stars || 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">المعدل العام</span>
                  <div className="flex items-center gap-2">
                    <Progress value={gameAvg} className="h-2 w-24" />
                    <span className="text-sm font-bold text-amber-500">{gameAvg}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Trend Chart */}
          <PerformanceTrendChart childId={selectedChildId} childName={selectedChild?.full_name} />

          {/* Footer */}
          <div className="text-center pt-2 border-t text-[10px] text-muted-foreground">
            <p>تم إنشاء هذا التقرير تلقائياً بتاريخ {format(new Date(), "d MMMM yyyy", { locale: ar })}</p>
          </div>
        </div>
      )}
    </div>
  );
}
